import React, { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PlatformShell from '../../components/PlatformShell';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { DataTable, StatCard, Badge } from '../../components/Ui';

function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN')}`;
}

export default function PlatformDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFromUrl = searchParams.get('status') || 'active';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(statusFromUrl);

  useEffect(() => {
    setStatusFilter(statusFromUrl);
  }, [statusFromUrl]);

  const onStatusChange = (value) => {
    setStatusFilter(value);
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'active') next.delete('status');
    else next.set('status', value);
    setSearchParams(next, { replace: true });
  };

  const { data: dash, loading } = useLiveData(() => ipApi.platformDashboard(), []);
  const includeArchived = statusFilter === 'archived' || statusFilter === 'all';
  const { data: hospData, reload } = useLiveData(
    () => ipApi.platformHospitals(includeArchived ? { includeArchived: true } : {}),
    [includeArchived],
  );
  const hospitals = hospData?.hospitals || [];

  const filtered = useMemo(() => {
    let list = hospitals;
    if (statusFilter === 'active') list = list.filter((h) => h.active !== false && !h.deletedAt);
    if (statusFilter === 'suspended') list = list.filter((h) => h.active === false && !h.deletedAt);
    if (statusFilter === 'archived') list = list.filter((h) => !!h.deletedAt);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((h) =>
        (h.name || '').toLowerCase().includes(q)
        || (h.city || '').toLowerCase().includes(q)
        || (h.ownerEmail || '').toLowerCase().includes(q)
        || (h.hospitalId || '').toLowerCase().includes(q));
    }
    return list;
  }, [hospitals, search, statusFilter]);

  const columns = [
    {
      key: 'name',
      label: 'Hospital',
      render: (r) => (
        <div>
          <Link to={`/platform/hospitals/${r.hospitalId}`}><strong>{r.name}</strong></Link>
          <div className="hint">{r.hospitalId}</div>
        </div>
      ),
    },
    { key: 'city', label: 'City' },
    { key: 'plan', label: 'Plan' },
    {
      key: 'pricing',
      label: 'HCEN / mo',
      render: (r) => formatInr(r.pricing?.monthlyFee),
    },
    {
      key: 'monthStats',
      label: 'OP revenue (month)',
      render: (r) => formatInr(r.monthStats?.opReceiptsTotal),
    },
    {
      key: 'monthStats2',
      label: 'Patients',
      render: (r) => r.monthStats?.patientCount ?? '—',
    },
    {
      key: 'quotas',
      label: 'Staff',
      render: (r) => {
        const q = r.quotas;
        if (!q) return '—';
        return `${q.totalUsed}/${q.totalMax || r.staffLimit || '—'}`;
      },
    },
    {
      key: 'op',
      label: 'OP link',
      render: (r) => (
        <Badge tone={r.opHospitalId ? 'success' : 'default'}>{r.opHospitalId ? 'Linked' : 'Not linked'}</Badge>
      ),
    },
    {
      key: 'ownerEmail',
      label: 'Owner',
      render: (r) => r.ownerEmail || '—',
    },
    {
      key: 'active',
      label: 'Status',
      render: (r) => {
        if (r.deletedAt) return <Badge tone="warning">Archived</Badge>;
        return <Badge tone={r.active !== false ? 'success' : 'warning'}>{r.active !== false ? 'Active' : 'Suspended'}</Badge>;
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions platform-row-actions">
          <Link to={`/platform/hospitals/${r.hospitalId}`} className="btn btn-sm btn-primary">Monitor</Link>
          <Link to={`/platform/hospitals/${r.hospitalId}?tab=staff`} className="btn btn-sm btn-secondary">Logins</Link>
          <Link to={`/platform/hospitals/${r.hospitalId}?tab=plan`} className="btn btn-sm btn-secondary">Plan</Link>
          <Link to={`/platform/hospitals/${r.hospitalId}?tab=oplink`} className="btn btn-sm btn-secondary">OP link</Link>
          <Link to={`/platform/analytics?hospitalId=${r.hospitalId}`} className="btn btn-sm btn-secondary">Revenue</Link>
        </div>
      ),
    },
  ];

  return (
    <PlatformShell title="HCEN Platform Admin" subtitle="Onboard, edit, monitor every hospital on Networks HMS">
      <div className="stat-grid">
        <StatCard label="Hospitals (live)" value={loading ? '…' : dash?.hospitalsTotal ?? 0} accent="#8b5cf6" />
        <StatCard label="Active" value={loading ? '…' : dash?.hospitalsActive ?? 0} accent="#10b981" />
        <StatCard label="MRR (HCEN fees)" value={loading ? '…' : formatInr(dash?.monthlyRevenueMrr)} accent="#6366f1" />
        <StatCard label="OP linked" value={loading ? '…' : dash?.opLinked ?? 0} accent="#0ea5e9" />
        <StatCard label="Staff accounts" value={loading ? '…' : dash?.staffTotal ?? 0} accent="#4f8ef7" />
        <StatCard label="Patients (all)" value={loading ? '…' : dash?.patientsTotal ?? 0} accent="#f97316" />
        <StatCard label="Suspended" value={loading ? '…' : dash?.hospitalsSuspended ?? 0} accent="#eab308" />
        <StatCard label="Archived" value={loading ? '…' : dash?.hospitalsArchived ?? 0} accent="#94a3b8" />
      </div>

      <div className="card mb platform-toolbar">
        <div className="platform-filters">
          <input
            className="input"
            placeholder="Search hospital, city, owner email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="input" value={statusFilter} onChange={(e) => onStatusChange(e.target.value)}>
            <option value="active">Active only</option>
            <option value="all">All (incl. suspended)</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived / removed</option>
          </select>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => reload()}>Refresh</button>
          <Link to="/platform/onboard" className="btn btn-primary btn-sm">+ Onboard hospital</Link>
        </div>
      </div>

      <div className="card">
        <h3>Hospitals on Networks HMS</h3>
        <p className="hint mb">Click <strong>Monitor</strong> for full hospital control — plan, limits, staff, revenue, OP link, suspend, or archive.</p>
        <DataTable columns={columns} rows={filtered} keyField="hospitalId" emptyMessage="No hospitals match your filters." />
      </div>
    </PlatformShell>
  );
}
